/**
 * Feature Manager for Questus Frontend
 * Manages feature flags and conditional rendering
 */

class FeatureManager {
    constructor() {
        this.features = null;
        this.mode = null;
        this.initialized = false;
    }

    async init(forceRefresh = false) {
        try {
            // Add cache-busting parameter if forcing refresh
            const url = forceRefresh 
                ? `/api/v1/features?t=${Date.now()}` 
                : '/api/v1/features';
            
            const response = await fetch(url, {
                cache: forceRefresh ? 'no-cache' : 'default'
            });
            const data = await response.json();
            
            this.mode = data.mode;
            this.features = data.features;
            this.initialized = true;
            
            
            // Auto-hide disabled features
            this.updateUI();
            
            // Dispatch event to notify that FeatureManager is ready
            document.dispatchEvent(new CustomEvent('FeatureManagerReady'));
            
            // Update folder tree immediately if folder data is available
            if (window.gFolderData) {
                setTimeout(() => this.updateFolderTree(), 100);
            }
            
            return true;
        } catch (error) {
            console.error('Failed to load feature flags:', error);
            return false;
        }
    }

    isEnabled(featureName) {
        if (!this.initialized) {
            console.warn('FeatureManager not initialized');
            return false;
        }
        
        const allFeatures = [
            ...(this.features['MVP Features'] || []),
            ...(this.features['Advanced Features'] || [])
        ];
        
        const feature = allFeatures.find(f => f.name === featureName);
        return feature ? feature.enabled : false;
    }

    needsDevWarning(featureName) {
        if (!this.initialized) {
            return false;
        }
        
        const allFeatures = [
            ...(this.features['MVP Features'] || []),
            ...(this.features['Advanced Features'] || [])
        ];
        
        const feature = allFeatures.find(f => f.name === featureName);
        return feature ? feature.needs_dev_warning : false;
    }

    isProductionReady(featureName) {
        if (!this.initialized) {
            return true;
        }
        
        const allFeatures = [
            ...(this.features['MVP Features'] || []),
            ...(this.features['Advanced Features'] || [])
        ];
        
        const feature = allFeatures.find(f => f.name === featureName);
        return feature ? feature.production_ready : true;
    }

    isMvpMode() {
        return this.mode === 'mvp';
    }

    isFullMode() {
        return this.mode === 'full';
    }

    ifEnabled(featureName, callback) {
        if (this.isEnabled(featureName)) {
            callback();
        }
    }

    updateUI() {
        
        // Hide elements with data-feature attribute if feature is disabled
        const featureElements = document.querySelectorAll('[data-feature]');
        
        featureElements.forEach(el => {
            const feature = el.getAttribute('data-feature');
            const isEnabled = this.isEnabled(feature);
            
            if (!isEnabled) {
                el.style.display = 'none';
                // Also disable tab functionality if it's a tab button
                if (el.classList.contains('nav-link')) {
                    el.setAttribute('tabindex', '-1');
                    el.classList.remove('active');
                    el.setAttribute('aria-selected', 'false');
                }
            } else {
                // Show element if feature is enabled - remove inline display style completely
                el.style.removeProperty('display');
                if (el.classList.contains('nav-link')) {
                    el.removeAttribute('tabindex');
                }
            }
        });
        
        // Hide elements with data-mode attribute if mode doesn't match
        const modeElements = document.querySelectorAll('[data-mode]');
        
        modeElements.forEach(el => {
            const mode = el.getAttribute('data-mode');
            if (mode !== this.mode) {
                el.style.display = 'none';
            } else {
                el.style.display = '';
            }
        });
        
        // Update folder tree to reflect feature changes
        this.updateFolderTree();
    }

    async updateFolderTree() {
        // Check if folder tree functionality is available
        if (window.gFolderData && typeof window.populateUI === 'function') {
            await window.populateUI();
        } else if (window.gFolderData) {
            // Try to import and call the populateUI function
            try {
                const { populateUI } = await import('./utils/ui/folderUI.js');
                await populateUI();
            } catch (error) {
                console.warn('FeatureManager: Could not update folder tree:', error);
            }
        }
    }

    // Utility function to show/hide tabs based on features
    updateTabs() {
        // Hide YouTube tab if feature is disabled
        if (!this.isEnabled('youtube_import')) {
            const youtubeTab = document.querySelector('[onclick*="youtube"]');
            if (youtubeTab) youtubeTab.style.display = 'none';
        }
        
        // Hide URL tab if feature is disabled
        if (!this.isEnabled('web_url_import')) {
            const urlTab = document.querySelector('[onclick*="url"]');
            if (urlTab) urlTab.style.display = 'none';
        }

        // Update deck navigation tabs based on feature flags
        // Try to import and call updateTabVisibility if available
        try {
            if (window.updateTabVisibility && typeof window.updateTabVisibility === 'function') {
                window.updateTabVisibility();
            } else {
                // Try dynamic import as fallback
                import('./utils/ui/tabs.js').then(({ updateTabVisibility }) => {
                    if (updateTabVisibility) updateTabVisibility();
                }).catch(() => {
                    // Silently fail if module not available
                });
            }
        } catch (error) {
            console.warn('Could not update tab visibility:', error);
        }
    }

    // Check if user can access a specific route
    canAccessRoute(route) {
        const routeFeatures = {
            '/worksheet': 'worksheet_mode',
            '/scenario': 'scenario_mode',
            '/analytics': 'analytics_dashboard',
            '/api': 'api_access',
            '/marketplace': 'content_marketplace'
        };
        
        const requiredFeature = routeFeatures[route];
        return requiredFeature ? this.isEnabled(requiredFeature) : true;
    }

    // Show upgrade prompt for disabled features
    showUpgradePrompt(featureName) {
        if (!this.isEnabled(featureName)) {
            alert(`This feature (${featureName}) is only available in FULL mode. Please upgrade to access it.`);
            return true;
        }
        return false;
    }
}

// Create global instance
window.featureManager = new FeatureManager();

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
    
    featureManager.init().then((success) => {
        if (success) {
            
            // Update tabs if on homepage
            if (document.getElementById('materialLoader')) {
                featureManager.updateTabs();
            }
            
            // Set up mutation observer to handle dynamically added elements
            const observer = new MutationObserver((mutations) => {
                let shouldUpdate = false;
                mutations.forEach((mutation) => {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === 1) { // Element node
                            if (node.hasAttribute('data-feature') || node.hasAttribute('data-mode')) {
                                shouldUpdate = true;
                            }
                            // Check children too
                            if (node.querySelectorAll && (node.querySelectorAll('[data-feature], [data-mode]').length > 0)) {
                                shouldUpdate = true;
                            }
                        }
                    });
                });
                
                if (shouldUpdate) {
                    featureManager.updateUI();
                }
            });
            
            // Start observing
            observer.observe(document.body, {
                childList: true,
                subtree: true
            });
            
            // Also update on htmx:afterSwap event if htmx is present
            if (window.htmx) {
                document.body.addEventListener('htmx:afterSwap', () => {
                    featureManager.updateUI();
                });
            }
        } else {
            console.error('FeatureManager: Failed to initialize');
        }
    }).catch(error => {
        console.error('FeatureManager: Error during initialization:', error);
    });
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
    module.exports = FeatureManager;
}